import {
  Component,
  OnInit,
  Input,
  OnChanges,
  ViewChild,
  AfterViewChecked,
  SimpleChanges,
  signal,
  computed,
} from '@angular/core';
import {
  CustomerModel,
  LocationModel,
  TotalSummaryModel,
  CUSTOMERTYPES,
  TYPE,
} from '@kanzi-apes/kanzi-models';
import { LocationListService } from '@kanzi-apes/kanzi-locations';
import { InventoryChartService } from '../../services';
import { EnvironmentService } from '@kanzi-apes/kanzi-utils';
import { EChartsOption } from 'echarts';
import { RegisteredSeriesOption } from 'echarts/types/dist/echarts';
import { cu } from '@fullcalendar/core/internal-common';

const emphasisStyle = {
  focus: 'series',
};

declare type Values<T> = T[keyof T];
declare type TSeries = Values<RegisteredSeriesOption>;
type ISeries = TSeries | TSeries[] | undefined;

@Component({
    selector: 'lib-kanzi-inventory-bar-chart',
    templateUrl: './kanzi-inventory-bar-chart.component.html',
    styleUrls: ['./kanzi-inventory-bar-chart.component.scss'],
    standalone: false
})
export class KanziInventoryBarChartComponent implements OnChanges {
  @Input() title: string = '';
  @Input() locationType: string | null = '';
  @Input() customer!: CustomerModel;
  // @ViewChild(DxChartComponent, { static: false }) chart!: DxChartComponent;

  modules = this.envService.environment!.modules;
  CTYPES = CUSTOMERTYPES;
  locations: LocationModel[] = [];
  totalsSummary: TotalSummaryModel[] = [];

  //New:
  public series = signal<ISeries>([]);
  public xSeries = signal<string[]>(['Presentes']);
  public xAxisData = signal<string[]>([]); //Bodegas
  public optionsCharts = signal<EChartsOption>({
    legend: {},
    tooltip: {},
    brush: {
      toolbox: ['rect'],
      xAxisIndex: 0,
    },
    toolbox: {
      feature: {
        magicType: {
          type: ['stack'],
        },
        dataView: {},
      },
    },
    xAxis: {
      type: 'category',
      data: [],
      axisLine: { onZero: true },
      splitLine: { show: false },
      splitArea: { show: false },
    },
    yAxis: {},
    series: [],
  });
  public updateOptions = computed<EChartsOption>(() => ({
    legend: {
      data: this.xSeries(),
      left: '10%',
    },
    xAxis: {
      data: this.xAxisData(),
      axisLine: { onZero: true },
      splitLine: { show: false },
      splitArea: { show: false },
    },
    series: this.series(),
  }));

  constructor(
    private locationService: LocationListService,
    private inventorySummary: InventoryChartService,
    private envService: EnvironmentService
  ) {}

  ngOnChanges() {
    this.totalsSummary = [];
    this.locationService
      .getlocationList({
        page: 1,
        page_size: 1000,
        customer_id: this.customer ? this.customer.id : null,
        type__name: this.locationType!,
      })
      .subscribe((locations) => {
        locations.results.forEach((location) => {
          this.inventorySummary
            .getStockLocationSummary({
              page: 1,
              page_size: 1,
              customer_id: this.customer ? this.customer.id : null,
              current_location_id: location.id,
            })
            .subscribe((stock) => {
              if (stock && stock.count > 0) {
                this.totalsSummary = this.totalsSummary.concat(
                  stock.results.map((result) => {
                    return {
                      ...result,
                      current_location_name:
                        result.current_location.display_name,
                    };
                  })
                );

                const locationsNames = [
                  ...new Set(
                    this.totalsSummary.map((t) => t.current_location_name)
                  ),
                ];

                if (locationsNames) {
                  this.xAxisData.set(locationsNames as string[]);
                }
                this.series.set(this.setSeries());
              }
            });
        });
      });
  }

  setSeries() {
    let series: TSeries[] = [
      {
        name: this.setNames('total_present'),
        type: 'bar',
        stack: 'total',
        emphasis: {
          focus: 'series',
        },
        data: this.filterByLocation(
          this.xAxisData(),
          'total_present',
          this.totalsSummary
        ),
      },
    ];

    if (this.modules.customerType !== this.CTYPES.ASSETS_NORFID) {
      this.xSeries.update((current) => [
        ...current,
        'En Transito',
        'Despachados',
        'Perdidos',
      ]);
      series = [
        ...series,
        {
          name: this.setNames('total_in_transfer'),
          type: 'bar',
          stack: 'total',
          emphasis: {
            focus: 'series',
          },
          data: this.filterByLocation(
            this.xAxisData(),
            'total_in_transfer',
            this.totalsSummary
          ),
        },
        {
          name: this.setNames('total_dispatch'),
          type: 'bar',
          stack: 'total',
          emphasis: {
            focus: 'series',
          },
          data: this.filterByLocation(
            this.xAxisData(),
            'total_dispatch',
            this.totalsSummary
          ),
        },
        {
          name: this.setNames('total_lost'),
          type: 'bar',
          stack: 'total',
          emphasis: {
            focus: 'series',
          },
          data: this.filterByLocation(
            this.xAxisData(),
            'total_lost',
            this.totalsSummary
          ),
        },
      ];
    }

    return series;
  }

  filterByLocation(
    locations: string[],
    key: keyof TotalSummaryModel,
    totalsSummary: TotalSummaryModel[]
  ) {
    const series: number[] = [];

    locations.forEach((location) => {
      const value = totalsSummary
        .filter((l) => l.current_location_name === location)
        .map((l) => l[key])
        .reduce((c: number, t: number) => c + t);

      series.push(value);
    });

    return series;
  }

  setNames(field: keyof TotalSummaryModel) {
    const keys = {
      total_in_transfer: 'En Transito',
      total_dispatch: 'Despachados',
      total_present: 'Presentes',
      total_lost: 'Perdidos',
    };

    return keys[field as keyof typeof keys];
  }
  customizeTooltip(arg: any) {
    return {
      text: arg.seriesName + ': ' + arg.valueText,
    };
  }
}
